import { useState, useRef, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { aiChat } from '@/lib/aiApi';

export default function ChatTab({ file, documentHash }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  async function send(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setSending(true);
    try {
      const payload = documentHash
        ? { documentHash, message: text, conversationId }
        : { file, message: text, conversationId };
      const data = await aiChat(payload);
      setConversationId(data.conversationId);
      setMessages((prev) => [...prev, { role: 'assistant', text: data.answer, citations: data.citations }]);
    } catch (err) {
      setError(err.message || 'Chat failed.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && !sending && (
          <div className="text-sm text-gray-500 text-center mt-6">
            Ask a question about your document.
            <br />
            <span className="text-xs text-gray-400">e.g. "What are the main conclusions?"</span>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`text-sm ${m.role === 'user' ? 'text-gray-900' : 'text-gray-800'}`}>
            <div className={`rounded-lg px-3 py-2 ${m.role === 'user' ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50 border border-gray-100'}`}>
              <div className="text-xs font-semibold text-gray-500 mb-1">
                {m.role === 'user' ? 'You' : 'Scrivar AI'}
              </div>
              <div className="whitespace-pre-wrap">{m.text}</div>
              {m.citations && m.citations.length > 0 && (
                <div className="mt-2 text-xs text-indigo-700">
                  Pages: {[...new Set(m.citations.map((c) => c.page))].sort((a, b) => a - b).join(', ')}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
          </div>
        )}
        {error && <div className="text-xs text-red-600">{error}</div>}
      </div>
      <form onSubmit={send} className="border-t border-gray-200 p-3">
        <div className="flex gap-2">
          <textarea
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { send(e); } }}
            placeholder="Ask about this document…"
            className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="rounded-lg bg-indigo-600 text-white px-3 text-sm font-semibold hover:bg-indigo-700 disabled:bg-indigo-300 self-stretch flex items-center"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
